import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "../lib/api";
import { MessageCircle, MapPin, BookHeart, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Members() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter((m) => [m.name, m.location, m.favorite_book, m.bio].some((f) => (f || "").toLowerCase().includes(q)))
    : members;

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C46B4E] font-semibold">The Collective</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Our members</h1>
        <p className="text-[#615A50] mt-2">The brothers and sisters in Christ who make this collective feel like home.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#615A50]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="member-search"
          placeholder="Search by name, place, or favorite book…"
          className="w-full rounded-full border border-[#D5CEBF] bg-white pl-10 pr-4 py-2.5 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((m) => (
          <div key={m.user_id} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-6 fade-up" data-testid={`member-${m.user_id}`}>
            <div className="flex items-center gap-4">
              {fileUrl(m.picture) ? <img src={fileUrl(m.picture)} alt={m.name} className="w-14 h-14 rounded-full object-cover" /> :
                <div className="w-14 h-14 rounded-full bg-[#3D5844] text-white flex items-center justify-center text-xl font-semibold">{m.name?.charAt(0)}</div>}
              <div>
                <h3 className="font-heading text-2xl text-[#2A2621]">{m.name}</h3>
                {m.location && <p className="text-sm text-[#615A50] flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {m.location}</p>}
              </div>
            </div>
            {m.bio && <p className="text-sm text-[#615A50] mt-4 leading-relaxed line-clamp-3">{m.bio}</p>}
            {m.favorite_book && <p className="text-sm text-[#3D5844] mt-3 flex items-center gap-1.5"><BookHeart className="w-4 h-4" /> {m.favorite_book}</p>}
            {user?.role === "admin" && m.user_id !== user.user_id && (
              <button onClick={() => navigate(`/messages?to=${m.user_id}`)} data-testid={`member-message-${m.user_id}`}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D5CEBF] text-sm font-medium text-[#2A2621] hover:bg-[#E7DFCE] transition-colors">
                <MessageCircle className="w-4 h-4" /> Message
              </button>
            )}
          </div>
        ))}
        {members.length === 0 && <p className="text-[#615A50]">No members yet.</p>}
        {members.length > 0 && filtered.length === 0 && <p className="text-[#615A50]" data-testid="member-search-empty">No members match "{query}".</p>}
      </div>
    </div>
  );
}
